import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { loadCurrentUser, logout, updateProfile } from "../../store/authSlice";
import {
  COUNTRY_CALLING_CODES,
  getCountryByIso,
  isoToFlagEmoji,
} from "../data/countryCallingCodes";
import { NAV } from "../Navbar/navbarTheme";
import { fetchUserBalance, fetchUserFiles } from "../utils/authApi";
import { fetchQuotes } from "../utils/shopApi";
import AuthFormLayout from "./AuthFormLayout";
import {
  AUTH_ACCENT,
  AUTH_PRIMARY,
  authAlertSx,
  authFieldSx,
  authFooterSecondaryButtonSx,
  authLogoutButtonSx,
  authSecondaryButtonSx,
  authSubmitButtonSx,
} from "./authStyles";

const LIMITS = {
  name: 60,
  company: 60,
  county: 60,
  phoneLocal: 20,
};

const allowedSimple = /^[a-zA-Z0-9 ]*$/;

const DASHBOARD_BG = "#eef1f6";

const dashboardPageSx = {
  minHeight: "100vh",
  bgcolor: DASHBOARD_BG,
  pt: { xs: "clamp(104px, 14vh, 150px)", md: "clamp(110px, 14vh, 150px)" },
  pb: { xs: 4, md: 6 },
  px: { xs: 2, sm: 3, md: 4 },
};

const dashboardShellSx = {
  maxWidth: 1180,
  mx: "auto",
};

const dashboardCardSx = {
  bgcolor: NAV.surface,
  borderRadius: 3,
  border: `1px solid ${NAV.line}`,
  boxShadow: "0 8px 28px rgba(15, 40, 71, 0.08)",
  overflow: "hidden",
};

const cardHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.75, sm: 2 },
  borderBottom: `1px solid ${NAV.line}`,
};

const cardHeaderIconSx = {
  width: 40,
  height: 40,
  borderRadius: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "rgba(30, 90, 158, 0.1)",
  color: AUTH_PRIMARY,
  flexShrink: 0,
};

const ProfileDataRow = ({ label, value, multiline = false }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems={multiline ? "flex-start" : "center"}
    spacing={2}
    sx={{
      py: 1.35,
      borderBottom: `1px solid ${NAV.line}`,
      "&:last-child": { borderBottom: 0 },
    }}
  >
    <Typography
      sx={{
        color: NAV.inkMuted,
        fontSize: "0.875rem",
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 700,
        color: NAV.ink,
        fontSize: "0.9375rem",
        textAlign: "right",
        whiteSpace: multiline ? "pre-line" : "normal",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Stack>
);

const findIsoByDial = (dial) => {
  if (!dial) return "GB";
  const match = COUNTRY_CALLING_CODES.find((country) => country.dial === dial);
  return match?.iso ?? "GB";
};

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) return "—";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatFileDate = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(date);
};

const formatBalance = (amount, currency = "GBP") => {
  const value = Number.parseFloat(String(amount ?? ""));
  if (!Number.isFinite(value)) return "—";

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency || "GBP"} ${value.toFixed(2)}`;
  }
};

const getBalanceFromUser = (profileUser) => {
  if (!profileUser || typeof profileUser !== "object") return null;

  const rawBalance =
    profileUser.balance ??
    profileUser.account_balance ??
    profileUser.credit_balance;
  const balance = Number.parseFloat(String(rawBalance ?? ""));
  if (!Number.isFinite(balance)) return null;

  return {
    balance,
    currency: String(profileUser.currency || "GBP").toUpperCase(),
  };
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, status } = useSelector((state) => state.auth);

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [county, setCounty] = useState("");
  const [countryIso, setCountryIso] = useState("GB");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasQuoteHistory, setHasQuoteHistory] = useState(false);
  const [userFiles, setUserFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState("");
  const [accountBalance, setAccountBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState("");

  const selectedCountry = useMemo(() => {
    return (
      getCountryByIso(countryIso) ??
      getCountryByIso("GB") ?? {
        iso: "GB",
        name: "United Kingdom",
        dial: "+44",
      }
    );
  }, [countryIso]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (token && !user && status !== "loading") {
      dispatch(loadCurrentUser());
    }
  }, [dispatch, token, user, status]);

  useEffect(() => {
    if (!token) {
      navigate("/SignIn", { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!user) return;
    setName(user.name ?? "");
    setCompany(user.company ?? "");
    setCounty(user.county ?? "");
    setCountryIso(findIsoByDial(user.country_code));
    setPhone(String(user.phone ?? "").replace(/\D/g, ""));
  }, [user]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const loadQuoteHistoryAvailability = async () => {
      try {
        const payload = await fetchQuotes({ page: 1 });
        if (cancelled) return;
        const total = payload?.meta?.total ?? payload?.data?.length ?? 0;
        setHasQuoteHistory(total > 0);
      } catch {
        if (!cancelled) {
          setHasQuoteHistory(false);
        }
      }
    };

    loadQuoteHistoryAvailability();

    return () => {
      cancelled = true;
    };
  }, [token, location.pathname]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const loadUserFiles = async () => {
      setFilesLoading(true);
      setFilesError("");
      try {
        const files = await fetchUserFiles();
        if (!cancelled) {
          setUserFiles(files);
        }
      } catch (loadError) {
        if (!cancelled) {
          setUserFiles([]);
          setFilesError(loadError?.message || "Unable to load your files.");
        }
      } finally {
        if (!cancelled) {
          setFilesLoading(false);
        }
      }
    };

    loadUserFiles();

    return () => {
      cancelled = true;
    };
  }, [token, location.pathname]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const loadAccountBalance = async () => {
      setBalanceLoading(true);
      setBalanceError("");

      const profileBalance = getBalanceFromUser(user);
      if (profileBalance && !cancelled) {
        setAccountBalance(profileBalance);
      }

      try {
        const balance = await fetchUserBalance();
        if (!cancelled && balance) {
          setAccountBalance(balance);
        }
      } catch (loadError) {
        if (!cancelled && !profileBalance) {
          setAccountBalance(null);
          setBalanceError(loadError?.message || "Unable to load your balance.");
        }
      } finally {
        if (!cancelled) {
          setBalanceLoading(false);
        }
      }
    };

    loadAccountBalance();

    return () => {
      cancelled = true;
    };
  }, [token, user, location.pathname]);

  const handleNameChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.name && allowedSimple.test(value)) {
      setName(value);
    }
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.company && allowedSimple.test(value)) {
      setCompany(value);
    }
  };

  const handleCountyChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.county && allowedSimple.test(value)) {
      setCounty(value);
    }
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    if (value.length <= LIMITS.phoneLocal) {
      setPhone(value);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/SignIn", { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    const trimmedName = name.trim();
    const trimmedCompany = company.trim();
    const trimmedCounty = county.trim();

    if (!trimmedName || !trimmedCompany || !trimmedCounty || !phone) {
      setError("Please complete all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        updateProfile({
          name: trimmedName,
          country_code: selectedCountry.dial,
          phone,
          company: trimmedCompany,
          county: trimmedCounty,
        }),
      ).unwrap();
      setSuccessMessage("Your profile has been updated.");
    } catch (submitError) {
      setError(
        submitError ||
          "Unable to update your profile right now. Please try again later.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return null;
  }

  if (status === "loading" || (status !== "failed" && !user)) {
    return (
      <Box
        component="main"
        sx={{
          ...dashboardPageSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: AUTH_PRIMARY }} />
      </Box>
    );
  }

  if (status === "failed" || !user) {
    return (
      <AuthFormLayout
        title="Profile"
        subtitle="Sign in to view your trade account."
        icon={PersonOutlineIcon}
        footer={
          <Button
            component={Link}
            to="/SignIn"
            variant="contained"
            fullWidth
            sx={authSubmitButtonSx}
          >
            Sign in
          </Button>
        }
      >
        <Alert severity="error" sx={authAlertSx}>
          Unable to load your profile. Please sign in again.
        </Alert>
      </AuthFormLayout>
    );
  }

  const displayPhone = [user.country_code, user.phone]
    .filter(Boolean)
    .join(" ");

  return (
    <Box component="main" sx={dashboardPageSx}>
      <Box sx={dashboardShellSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.75rem", sm: "2rem" },
                color: NAV.ink,
                lineHeight: 1.15,
              }}
            >
              Customer account
            </Typography>
            <Typography
              sx={{ color: NAV.inkMuted, mt: 0.5, fontSize: "0.9375rem" }}
            >
              {user.company || "Trade account"} · {user.email}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              component={Link}
              to="/"
              variant="outlined"
              sx={{
                ...authFooterSecondaryButtonSx,
                mt: 0,
                flex: { xs: 1, sm: "none" },
              }}
            >
              Back to home
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleLogout}
              sx={{ ...authLogoutButtonSx, mt: 0, flex: { xs: 1, sm: "none" } }}
            >
              Log out
            </Button>
          </Stack>
        </Stack>

        {successMessage ? (
          <Alert severity="success" sx={{ ...authAlertSx, mb: 2.5 }}>
            {successMessage}
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ ...authAlertSx, mb: 2.5 }}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.35fr 1fr" },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          <Box sx={dashboardCardSx}>
            <Box sx={cardHeaderSx}>
              <Box sx={cardHeaderIconSx}>
                <PersonOutlineIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography
                sx={{ fontWeight: 800, fontSize: "1.0625rem", color: NAV.ink }}
              >
                Customer profile
              </Typography>
            </Box>
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}>
              <ProfileDataRow label="Customer name" value={user.name} />
              <ProfileDataRow label="Company name" value={user.company} />
              <ProfileDataRow label="County" value={user.county} multiline />
              <ProfileDataRow label="Email" value={user.email} />
              <ProfileDataRow label="Phone" value={displayPhone} />
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: 3,
              p: { xs: 2, sm: 2.5 },
              minHeight: { lg: 220 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              color: "#fff",
              background: `linear-gradient(135deg, ${AUTH_PRIMARY} 0%, #2563eb 100%)`,
              boxShadow: "0 14px 36px rgba(30, 90, 158, 0.28)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <ShowChartOutlinedIcon
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                fontSize: 88,
                opacity: 0.14,
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: "1.5rem" }}>
                  £
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.875rem", opacity: 0.9, mb: 0.5 }}>
                Account balance
              </Typography>
              {balanceLoading && !accountBalance ? (
                <CircularProgress size={28} sx={{ color: "#fff", my: 1 }} />
              ) : accountBalance ? (
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "2rem", sm: "2.25rem" },
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {formatBalance(
                    accountBalance.balance,
                    accountBalance.currency,
                  )}
                </Typography>
              ) : balanceError ? (
                <Typography sx={{ fontSize: "0.9375rem", opacity: 0.92 }}>
                  {balanceError}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
                  —
                </Typography>
              )}
              <Typography sx={{ fontSize: "0.8125rem", opacity: 0.82, mt: 1 }}>
                Available credit
              </Typography>
            </Box>
          </Box>
        </Box>

        {hasQuoteHistory ? (
          <Box sx={{ ...dashboardCardSx, mb: 2.5 }}>
            <Box
              sx={{
                ...cardHeaderSx,
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box sx={cardHeaderIconSx}>
                  <HistoryOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.0625rem",
                    color: NAV.ink,
                  }}
                >
                  Requested quotes
                </Typography>
              </Stack>
              <Button
                component={Link}
                to="/QuoteHistory"
                variant="contained"
                sx={{
                  ...authSubmitButtonSx,
                  mt: 0,
                  bgcolor: AUTH_PRIMARY,
                  boxShadow: "0 8px 20px rgba(30, 90, 158, 0.25)",
                  "&:hover": { bgcolor: "#163a5e" },
                }}
              >
                View all quotes
              </Button>
            </Box>
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
              <Typography
                sx={{
                  color: NAV.inkMuted,
                  fontSize: "0.9375rem",
                  lineHeight: 1.6,
                }}
              >
                Review your submitted quote requests, track their status, and
                open quote documents.
              </Typography>
            </Box>
          </Box>
        ) : null}

        <Box sx={{ ...dashboardCardSx, mb: 2.5 }}>
          <Box sx={cardHeaderSx}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={cardHeaderIconSx}>
                <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography
                sx={{ fontWeight: 800, fontSize: "1.0625rem", color: NAV.ink }}
              >
                Invoices & documents
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}>
            {filesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: AUTH_PRIMARY }} />
              </Box>
            ) : filesError ? (
              <Alert severity="warning" sx={authAlertSx}>
                {filesError}
              </Alert>
            ) : userFiles.length === 0 ? (
              <Box
                sx={{
                  border: `1px dashed ${NAV.line}`,
                  borderRadius: 2,
                  bgcolor: NAV.surfaceSubtle,
                  py: 4,
                  px: 2,
                  textAlign: "center",
                }}
              >
                <InsertDriveFileOutlinedIcon
                  sx={{ color: NAV.inkMuted, fontSize: 36, mb: 1 }}
                />
                <Typography sx={{ color: NAV.inkMuted, fontSize: "0.9375rem" }}>
                  No documents have been shared with your account yet.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={0}>
                {userFiles.map((file, index) => (
                  <Box
                    key={file.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 1.5,
                      borderBottom:
                        index < userFiles.length - 1
                          ? `1px solid ${NAV.line}`
                          : "none",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: "rgba(227, 24, 55, 0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon
                        sx={{ color: AUTH_ACCENT, fontSize: 20 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          color: NAV.ink,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: NAV.inkMuted,
                          mt: 0.25,
                        }}
                      >
                        {formatFileSize(file.size)}
                        {file.created_at
                          ? ` · ${formatFileDate(file.created_at)}`
                          : ""}
                      </Typography>
                    </Box>
                    {file.url ? (
                      <Button
                        component="a"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                        endIcon={
                          <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                        }
                        sx={{
                          ...authSecondaryButtonSx,
                          mt: 0,
                          flexShrink: 0,
                          fontSize: "0.75rem",
                        }}
                      >
                        Open
                      </Button>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Box>

        <Box sx={dashboardCardSx}>
          <Box sx={cardHeaderSx}>
            <Box sx={cardHeaderIconSx}>
              <EditOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography
              sx={{ fontWeight: 800, fontSize: "1.0625rem", color: NAV.ink }}
            >
              Update profile
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    required
                    label="Full name"
                    value={name}
                    onChange={handleNameChange}
                    sx={authFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon
                            sx={{ color: NAV.inkMuted, fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Email address"
                    value={user.email ?? ""}
                    disabled
                    sx={authFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon
                            sx={{ color: NAV.inkMuted, fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    required
                    label="Company name"
                    value={company}
                    onChange={handleCompanyChange}
                    sx={authFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessOutlinedIcon
                            sx={{ color: NAV.inkMuted, fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    required
                    label="County"
                    value={county}
                    onChange={handleCountyChange}
                    sx={authFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnOutlinedIcon
                            sx={{ color: NAV.inkMuted, fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl fullWidth size="small" sx={authFieldSx}>
                    <InputLabel id="profile-country-label">
                      Country code
                    </InputLabel>
                    <Select
                      labelId="profile-country-label"
                      value={countryIso}
                      label="Country code"
                      onChange={(event) => setCountryIso(event.target.value)}
                    >
                      {COUNTRY_CALLING_CODES.map((country) => (
                        <MenuItem key={country.iso} value={country.iso}>
                          {isoToFlagEmoji(country.iso)} {country.name} (
                          {country.dial})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    required
                    label="Phone number"
                    value={phone}
                    onChange={handlePhoneChange}
                    sx={authFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon
                            sx={{ color: NAV.inkMuted, fontSize: 20 }}
                          />
                          <Typography
                            sx={{
                              ml: 0.5,
                              fontSize: "0.875rem",
                              color: NAV.inkMuted,
                              fontWeight: 600,
                            }}
                          >
                            {selectedCountry.dial}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    ...authSubmitButtonSx,
                    alignSelf: { sm: "flex-start" },
                    minWidth: { sm: 200 },
                  }}
                >
                  {isSubmitting ? "Saving..." : "Save changes"}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfilePage;
